import {PATH_SEP} from "~/constants";
import {m_taxonomy} from "~/services/classes/TaxonomyClasses";

/** One entry in Before / After: the changed level (or field value) shown, and the full path on hover. */
export type TaxonomyChangePart = {
    label: string;
    title: string;
};

/**
 * One row per changed category: every change the backend records on it (`values.changes` in
 * `diff_tree`), compared with the taxonomy's first saved state (operation 1), matched by `sources`.
 * A rename with a new description, or a rename and then a delete, is one row with several types.
 * Reverted changes are left out, and so are categories added and then deleted in the same draft.
 */
export type TaxonomyChangeRow = {
    id: string;
    /** The changed category */
    nodeId: number;
    /** Its changes, in the backend's order. */
    changes: m_taxonomy.Change[];
    /** The most significant change type: it colours the row's picture. */
    type: m_taxonomy.ChangeType;
    /** Before the changes: the old paths and old field values; empty for an added category. */
    before: TaxonomyChangePart[];
    /** After the changes: the current path and new field values; empty for a deleted category. */
    after: TaxonomyChangePart[];
    /** Descriptions before each description change (one per merged category in a merge); empty if unchanged. */
    previousDescriptions: string[];
    /** The description now; undefined if unchanged or the category was deleted. */
    currentDescription?: string;
    /** `p__id__count`: the category's transactions. */
    transactions: number;
    /** User ids who edited the category (`values.authors`). */
    authors: number[];
};

const humanize = (field: string) => field.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
const EMPTY = '(empty)';
/** Description changes have their own columns, not Before / After. */
const isDescription = (change: m_taxonomy.Change) => change.type === 'updated' && change.field === 'description';

/**
 * A move's `original_location`: labels down to the category itself (its old name last). A leading
 * taxonomy-root label, if the backend includes it, is dropped so paths start at L1 like ours.
 */
export function originalPath(root: m_taxonomy.Tree, change: {original_location: string[]}): string[] {
    const path = change.original_location;
    return path.length > 1 && path[0] === root.label ? path.slice(1) : path;
}

/** The first change of a type, typed. */
export function findChange<T extends m_taxonomy.ChangeType>(
    changes: m_taxonomy.Change[],
    type: T,
): Extract<m_taxonomy.Change, {type: T}> | undefined {
    return changes.find((c): c is Extract<m_taxonomy.Change, {type: T}> => c.type === type);
}

/** Every change of a type, typed. */
export function findChanges<T extends m_taxonomy.ChangeType>(
    changes: m_taxonomy.Change[],
    type: T,
): Extract<m_taxonomy.Change, {type: T}>[] {
    return changes.filter((c): c is Extract<m_taxonomy.Change, {type: T}> => c.type === type);
}

/**
 * A path shown as the levels that differ from the other path, from the first one down, e.g.
 * "Professional & Admin Services › Royalty & Licensing Fees" for a move and rename, or "Oil" for a rename;
 * the last level when nothing differs (a deleted or added category). The full path is on hover.
 */
const pathPart = (shown: string[], other: string[]): TaxonomyChangePart => {
    const firstDiff = shown.findIndex((label, i) => label !== other[i]);
    const from = firstDiff === -1 ? shown.length - 1 : firstDiff;
    return {label: shown.slice(from).join(PATH_SEP), title: shown.join(PATH_SEP)};
};
const textPart = (text: string): TaxonomyChangePart => ({label: text, title: text});
/** How many levels a path part shows */
const levels = (part: TaxonomyChangePart) => part.label.split(PATH_SEP).length;

/** What a change was before: the old path, e.g. "Indirect Spend › Energy › Oil"; nothing for an added category. */
function beforeOf(root: m_taxonomy.Tree, change: m_taxonomy.Change, parents: string[], path: string[]): TaxonomyChangePart | undefined {
    switch (change.type) {
        case 'added': return undefined;
        case 'removed': return pathPart(path, path);
        case 'renamed': return pathPart([...parents, change.original_label], path);
        case 'moved': return pathPart(originalPath(root, change), path);
        case 'updated': return textPart(`${humanize(change.field)}: ${change.original_value || EMPTY}`);
    }
}

/**
 * The most significant change type, which colours the row's picture. A merged category (several
 * `sources`) gets a move for each merged category that came from another parent: the picture colours
 * those merged categories as moved, so the merged one itself is not.
 */
function mainType(node: m_taxonomy.Tree, changes: m_taxonomy.Change[]): m_taxonomy.ChangeType {
    const isMerge = node.sources.length > 1;
    return m_taxonomy.CHANGE_ORDER.find(type => (type !== 'moved' || !isMerge) && changes.some(c => c.type === type))
        ?? changes[0].type;
}

export function listTaxonomyChanges(root: m_taxonomy.Tree): TaxonomyChangeRow[] {
    const rows: TaxonomyChangeRow[] = [];
    // Paths start at L1: the root is the taxonomy itself.
    const visit = (node: m_taxonomy.Tree, parents: string[]) => {
        const path = [...parents, node.label];
        const changes = (node.values.changes ?? []).filter(c => !c.reverted);
        // Added and then deleted in this draft: the backend still says "added", but there is nothing to
        // approve and the builder's tree no longer shows it (nor anything added under it).
        const addedThenDeleted = node.removed && changes.some(c => c.type === 'added');
        if (changes.length > 0 && !addedThenDeleted) {
            const added = findChange(changes, 'added');
            const removed = findChange(changes, 'removed');

            // Before: every change, in the backend's order (a merge records a rename, description update
            // and move per merged category), each with what it was before. An old category shows once: a
            // rename and a move of it have the same old name, and the move's path (the real old location) wins.
            const byOldName = new Map<string, TaxonomyChangePart>();
            if (!added) {
                for (const change of changes.filter(c => !isDescription(c))) {
                    const part = beforeOf(root, change, parents, path);
                    const oldName = part?.title.split(PATH_SEP).at(-1) ?? '';
                    if (part && (!byOldName.has(oldName) || change.type === 'moved')) byOldName.set(oldName, part);
                }
            }
            const before = [...byOldName.values()];

            // After: the category as it is now, each changed field once.
            const pathChanged = changes.some(c => c.type !== 'updated');
            const fields = [...new Set(findChanges(changes, 'updated').filter(u => !isDescription(u)).map(u => u.field))];
            // The current path, as the levels that differ from the old path(s); with several (a merge), the one
            // differing highest up, so every changed level shows. An added category shows its name.
            const oldPaths = changes.flatMap(c => c.type === 'moved' ? [originalPath(root, c)]
                : c.type === 'renamed' ? [[...parents, c.original_label]] : []);
            const currentPart = (oldPaths.length ? oldPaths : [path])
                .map(oldPath => pathPart(path, oldPath))
                .reduce((most, part) => levels(part) > levels(most) ? part : most);
            const afterParts = removed ? [] : [
                ...(pathChanged ? [currentPart] : []),
                ...fields.map(field => {
                    const now = node.values[field as keyof m_taxonomy.Data];
                    return textPart(`${humanize(field)}: ${typeof now === 'string' && now ? now : EMPTY}`);
                }),
            ];

            // Descriptions: every previous one, in order; the current one once.
            const descriptionChanges = findChanges(changes, 'updated').filter(isDescription);
            const previousDescriptions = descriptionChanges.map(u => u.original_value || EMPTY);
            const currentDescription = descriptionChanges.length && !removed
                ? node.values.description || EMPTY
                : undefined;

            rows.push({
                id: String(node.id),
                nodeId: node.id,
                changes,
                type: mainType(node, changes),
                before,
                after: afterParts,
                previousDescriptions,
                currentDescription,
                transactions: node.values.p__id__count ?? 0,
                authors: node.values.authors ?? [],
            });
        }
        node.children.forEach(child => visit(child, path));
    };
    root.children.forEach(child => visit(child, []));
    return rows;
}
